package editor.ui

import common.dto.elementClasses
import editor.EditorManager
import kotlinx.browser.document
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import kotlinx.html.js.onMouseEnterFunction
import kotlinx.html.js.onMouseLeaveFunction
import org.w3c.dom.HTMLElement

data class DropdownItem(val name: String, val callback: () -> Unit)

/**
 * A type of control that shows a list of options expanded towards the bottom when hovered.
 * For brick dropdowns, Speichern/Laden, etc.
 *
 * In the editor for the Speichern/Laden dropdown, the CSS is additionally customized
 * for a "floating content on the left" view in collapsed control mode. See [EditorHtml] and editor.css.
 *
 * Due to the non-reactive nature of the HTML UI, updating the dropdown state isn't that convenient.
 */
object DropdownManager {
    fun TagConsumer<HTMLElement>.dropdown(
        name: String,
        reversible: Boolean = false,
        additionalClasses: Set<String> = emptySet(),
        content: List<DropdownItem>,
    ) = div {
        val id = EditorUiHelper.idForName(name)
        fun close() = document.querySelector(".control-$id .dropdown-content")?.classList?.remove("visible")

        onMouseEnterFunction = {
            document.querySelector(".control-$id .dropdown-content")?.classList?.add("visible")
        }

        onMouseLeaveFunction = {
            close()
        }

        classes = buildSet {
            add("dropdown")
            add("control-$id")
            add(id)
            add("control")
            add("hoverable")
            addAll(additionalClasses)
            if (reversible) add("reversible")
        }

        p { +name }
        img(
            classes = "icon", src = "icons/$id.svg", alt = name
        )

        div("dropdown-content") {
            content.forEach { (itemName, callback) ->
                div("dropdown-item hoverable item-$itemName") {
                    p { +itemName }
                    onClickFunction = {
                        close()
                        callback()
                    }
                }
            }
        }
    }

    fun TagConsumer<HTMLElement>.brickDropdown(
        elementClass: String,
        reversible: Boolean = false,
        additionalClasses: Set<String> = emptySet(),
    ) = dropdown(
        elementClass,
        reversible,
        additionalClasses = buildSet { 
            addAll(additionalClasses)
            add("brick")
            if (EditorManager.toBePlacedType in elementClasses[elementClass]!!) add("selected")
        },
        elementClasses[elementClass]!!.sortedBy { it.lengthMultiplier }.map { elementType ->
            DropdownItem(elementType.name) {
                EditorManager.toBePlacedType = elementType
            }
        }
    )
}